import { type ChangeEvent, useCallback, useMemo } from 'react';
import type { Lens } from '@hookform/lenses';
import { zodResolver } from '@hookform/resolvers/zod';
import { useFormContext, useWatch } from 'react-hook-form';
import { match } from 'ts-pattern';
import { z } from 'zod/v4';
import { useButtonState } from '@interxfi/design-system/components/button/button-state';
import { useMergedDialogOpenProps } from '@interxfi/design-system/components/dialog/utils';
import { FancyRadioGroupInput, type FancyRadioGroupOption } from '@interxfi/design-system/components/input/fancy/radio/fancy-radio-group-input';
import { defaultValueMapper, FormWizard, type FormWizardDrawerProps } from '../../../../../../components/form/drawer/wizard/form-wizard';
import { useFormWizardContextValue, useFormWizardSetStepValue } from '../../../../../../components/form/drawer/wizard/form-wizard-context';
import type { FormWizardReducerOutput } from '../../../../../../components/form/drawer/wizard/step/form-wizard-form-step';
import { useFormWizardFormStepContext } from '../../../../../../components/form/drawer/wizard/step/form-wizard-form-step-context';
import type { FormWizardReviewStep } from '../../../../../../components/form/drawer/wizard/step/form-wizard-review-step';
import type { StepArray } from '../../../../../../components/form/drawer/wizard/step/form-wizard-step-builder';
import { directBeneficialOwnerControlPersonFormDefaultValues } from './beneficial-owner/direct-beneficial-owner-control-person-form';
import { indirectBeneficialOwnerControlPersonFormDefaultValues } from './beneficial-owner/indirect-beneficial-owner-control-person-form';
import {
  ControlPersonForm,
  type ControlPersonFormValues,
  controlPersonFormSchema,
  mapControlPersonFormValuesToConfirmationDetails,
} from './control-person-form';
import { directorControlPersonFormDefaultValues } from './director-control-person-form';
import { officerControlPersonFormDefaultValues } from './officer-control-person-form';
import { otherControlPersonFormDefaultValues } from './other-control-person-form';

const kindSelectFormSchema = z.object({
  kind: z.enum(controlPersonFormSchema.def.options.flatMap((opt) => opt.def.shape.kind.def.values)),
});

type ControlPersonKindSelectFormValues = z.infer<typeof kindSelectFormSchema>;

type FormSteps = { kindSelect: ControlPersonKindSelectFormValues; main: ControlPersonFormValues };

interface FormContext {
  programId: string | undefined;
}

const defaultEmptyValues: FormSteps = {
  kindSelect: { kind: undefined as unknown as ControlPersonFormValues['kind'] },
  main: {} as ControlPersonFormValues,
};

const kindOptions = [
  {
    label: 'Direct Beneficial Owner',
    value: 'directBeneficialOwner',
    subTitle: 'Individual who owns 25% or more of the organization',
  },
  {
    label: 'Indirect Beneficial Owner',
    value: 'indirectBeneficialOwner',
    subTitle: 'Individual who owns 25% or more of the organization through another business or entity.',
  },
  {
    label: 'Officer',
    value: 'officer',
    subTitle:
      'Individual with significant responsibility to control manage, or direct the organization (e.g., CEO, CFO, General Partner, Managing Member)',
  },
  {
    label: 'Director',
    value: 'director',
    subTitle: 'Board members with governance authority',
  },
  {
    label: 'Other',
    value: 'other',
    subTitle:
      "Individual with decision-making influence or substantial control over the organization's operations (e.g., largest single shareholder or board chairperson)",
  },
] as const satisfies FancyRadioGroupOption<ControlPersonFormValues['kind']>[];

function goBackToKindSelectReducer(_state: FormSteps, context: FormContext | undefined): FormWizardReducerOutput<FormSteps, FormContext> {
  return { steps: { ...defaultEmptyValues }, context };
}

const reviewStep: FormWizardReviewStep<FormSteps, FormContext> = {
  kind: 'review',
  buildConfirmationDetails: (values) => {
    return mapControlPersonFormValuesToConfirmationDetails(values.main);
  },
  saveCta: 'Add Control Person',
};

function KindSelectForm({ lens }: { lens: Lens<ControlPersonKindSelectFormValues> }) {
  const set = useFormWizardSetStepValue<FormSteps>();
  const { setValue } = useFormContext();
  const { formRef } = useFormWizardFormStepContext();
  const kindInterop = lens.focus('kind').interop();
  const selectedKind = useWatch(kindInterop);

  const handleSelectKind = useCallback(
    (e: ChangeEvent<HTMLInputElement>) => {
      const newKind = e.target.value as ControlPersonFormValues['kind'];
      setValue(kindInterop.name, newKind, { shouldDirty: true, shouldTouch: true });

      const value = match(newKind)
        .with('directBeneficialOwner', () => directBeneficialOwnerControlPersonFormDefaultValues)
        .with('indirectBeneficialOwner', () => indirectBeneficialOwnerControlPersonFormDefaultValues)
        .with('officer', () => officerControlPersonFormDefaultValues)
        .with('director', () => directorControlPersonFormDefaultValues)
        .with('other', () => otherControlPersonFormDefaultValues)
        .exhaustive(() => undefined);

      if (value) {
        set('main', value);
        formRef?.requestSubmit();
      }
    },
    [set, formRef, setValue, kindInterop.name],
  );

  return (
    <FancyRadioGroupInput
      hideLabel
      label="Control Person Type"
      name={kindInterop.name}
      onChange={handleSelectKind}
      options={kindOptions}
      size="large"
      value={selectedKind}
    />
  );
}

function MainForm({ lens }: { lens: Lens<ControlPersonFormValues> }) {
  const ctx = useFormWizardContextValue<FormSteps, FormContext>();
  return <ControlPersonForm lens={lens} programId={ctx?.programId} />;
}

const kindSelectStep = {
  id: 'kindSelect',
  kind: 'form',
  schema: zodResolver(kindSelectFormSchema),
  children: KindSelectForm,
  hideButtonRow: true,
} as const;

const mainStep = {
  id: 'main',
  kind: 'form',
  schema: zodResolver(controlPersonFormSchema),
  onGoBackReducer: goBackToKindSelectReducer,
  children: MainForm,
} as const satisfies StepArray<FormSteps, FormContext>[number];

const initialSteps: StepArray<FormSteps, FormContext> = [kindSelectStep, mainStep] as const;

function getTitle(values: FormSteps): string {
  return match(values.kindSelect.kind)
    .with('directBeneficialOwner', () => 'Add Direct Beneficial Owner')
    .with('indirectBeneficialOwner', () => 'Add Indirect Beneficial Owner')
    .with('officer', () => 'Add Officer')
    .with('director', () => 'Add Director')
    .with('other', () => 'Add Other Control Person')
    .exhaustive(() => 'Add Control Person');
}

interface AddControlPersonDrawerProps extends FormWizardDrawerProps {
  handleSubmit: (person: ControlPersonFormValues) => void;
  programId: string | undefined;
}

export function AddControlPersonFormDrawer({
  handleSubmit,
  isOpen: providedIsOpen,
  onOpenChange: providedOnOpenChange,
  programId,
  ...rest
}: AddControlPersonDrawerProps) {
  const [isOpen, onOpenChange] = useMergedDialogOpenProps({ isOpen: providedIsOpen, onOpenChange: providedOnOpenChange });
  const [buttonState, buttonRef] = useButtonState(false, undefined);
  const initialContext: FormContext = useMemo(() => ({ programId }), [programId]);

  const handleSave = useCallback(
    (values: FormSteps) => {
      handleSubmit(values.main);
    },
    [handleSubmit],
  );

  return (
    <FormWizard<FormSteps, FormSteps, FormContext>
      description="Form to add a control person"
      handleMapFormData={defaultValueMapper}
      initialContext={initialContext}
      initialData={defaultEmptyValues}
      initialSteps={initialSteps}
      isOpen={isOpen}
      onComplete={handleSave}
      onOpenChange={onOpenChange}
      reviewStep={reviewStep}
      saveCtaRef={buttonRef}
      saveCtaState={buttonState}
      title={getTitle}
      {...rest}
    />
  );
}
